import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Skeleton,
  Breadcrumbs,
  Link,
  Button,
  useTheme,
  alpha
} from '@mui/material';
import {
  Home as HomeIcon,
  ChevronRight as ChevronRightIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { instructorAPI } from '../services/api';
import InstructorCard from '../components/InstructorCard';

const Instructors = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInstructors = async () => {
      try {
        setLoading(true);
        const response = await instructorAPI.getAll();
        if (response.success) {
          setInstructors(response.data || []);
        } else {
          setError('Failed to load instructors');
        }
      } catch (err) {
        console.error('Failed to fetch instructors:', err);
        setError('Failed to load instructors. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchInstructors();
  }, []);

  const handleInstructorClick = (id) => {
    navigate(`/instructor/${id}`);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 8 }}>
      <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          sx={{ mb: 3 }}
          separator={<ChevronRightIcon sx={{ fontSize: 14, opacity: 0.5 }} />}
        >
          <Link
            component={RouterLink}
            to="/"
            underline="hover"
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'text.secondary',
              fontSize: '0.8125rem',
              fontWeight: 500,
              '&:hover': { color: 'primary.main' },
            }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Home
          </Link>
          <Link
            component={RouterLink}
            to="/instructors"
            underline="hover"
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'text.primary',
              fontSize: '0.8125rem',
              fontWeight: 600,
            }}
          >
            <PersonIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Instructors
          </Link>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 5, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.5px', mb: 1.5 }}>
            Meet Our Instructors
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Learn from our team of experienced and certified language instructors dedicated to helping you achieve your goals.
          </Typography>
        </Box>

        {/* Content */}
        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 3,
                  }}
                >
                  <Skeleton variant="circular" width={100} height={100} sx={{ mb: 2 }} />
                  <Skeleton variant="text" width="60%" height={28} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="40%" height={20} sx={{ mb: 2 }} />
                  <Skeleton variant="rounded" width="100%" height={36} />
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="error" gutterBottom>{error}</Typography>
            <Button variant="outlined" onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </Box>
        ) : instructors.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="text.secondary">No instructors found.</Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {instructors.map((instructor) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={instructor.id}>
                <InstructorCard teacher={instructor} sx={{ width: '100%' }} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default Instructors;
